import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Clock, ArrowRight } from "lucide-react";

const FADE_UP_LARGE = { opacity: 0, y: 40 };
const FADE_IN_PLACE = { opacity: 1, y: 0 };
const VIEWPORT_ONCE = { once: true };
const TRANSITION_DEFAULT = { duration: 0.6 };

/**
 * A single service row on the Services page. Kept at module scope so React can
 * reconcile it correctly between renders (avoids the react/no-unstable-nested
 * components warning we were hitting when this lived inside ServicesPage).
 */
export const ServiceCard = ({ service, index, isB2B = false }) => {
  const reversed = index % 2 === 1;
  return (
    <motion.div
      initial={FADE_UP_LARGE}
      whileInView={FADE_IN_PLACE}
      viewport={VIEWPORT_ONCE}
      transition={TRANSITION_DEFAULT}
      className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center"
    >
      <div className={reversed ? "lg:order-2" : ""}>
        <div className="relative rounded-3xl overflow-hidden aspect-[4/3]">
          <img
            src={service.image_url}
            alt={service.name}
            className="w-full h-full object-cover"
          />
          {!isB2B && (
            <div className="absolute top-4 right-4 bg-white/90 backdrop-blur-sm rounded-full px-4 py-2">
              <span className="text-[#2D5016] font-semibold">From £{service.base_price}</span>
            </div>
          )}
        </div>
      </div>

      <div className={reversed ? "lg:order-1" : ""}>
        <div className="flex items-center gap-2 mb-3">
          <span className="text-sm text-[#7CB342] font-semibold uppercase tracking-widest">
            {isB2B ? "Contract Clean" : "Residential"}
          </span>
        </div>
        <h2 className="text-3xl md:text-4xl font-bold text-[#1A2E12] mb-4">{service.name}</h2>
        <p className="text-lg text-[#5C6B57] mb-6 leading-relaxed">{service.description}</p>

        <ul className="space-y-3 mb-8">
          {service.features?.slice(0, 5).map((feature) => (
            <li key={`feature-${feature}`} className="flex items-start gap-3">
              <CheckCircle2 className="w-5 h-5 text-[#7CB342] flex-shrink-0 mt-0.5" />
              <span className="text-[#5C6B57]">{feature}</span>
            </li>
          ))}
        </ul>

        {!isB2B && (
          <div className="flex items-center gap-4 text-sm text-[#5C6B57] mb-8">
            <div className="flex items-center gap-2">
              <Clock className="w-4 h-4" />
              <span>{service.duration_estimate}</span>
            </div>
          </div>
        )}
        {isB2B && (
          <div className="flex items-center gap-2 text-sm text-[#5C6B57] mb-8">
            <Clock className="w-4 h-4" />
            <span>Tailored to your site &amp; schedule</span>
          </div>
        )}

        <Link to={`/booking?service=${service.slug}`}>
          <Button
            className="bg-[#2D5016] hover:bg-[#1A2E12] text-white rounded-full px-8 py-6 text-lg btn-primary"
            data-testid={`book-${service.slug}`}
          >
            {isB2B ? "Get a FREE Quote" : "Book Now"}
            <ArrowRight className="ml-2 w-5 h-5" />
          </Button>
        </Link>
      </div>
    </motion.div>
  );
};

export default ServiceCard;
